import React, { useEffect, useState, useCallback } from 'react';
import { outboundApi } from '../api/client';
import LoadingSpinner from '../components/LoadingSpinner';

const STATUS_LABELS = {
  queued:  { label: 'Warteschlange', cls: 'bg-yellow-50 text-yellow-700' },
  sending: { label: 'Wird gesendet', cls: 'bg-blue-50 text-blue-700' },
  sent:    { label: 'Gesendet',      cls: 'bg-green-50 text-green-700' },
  failed:  { label: 'Fehlgeschlagen',cls: 'bg-red-50 text-red-700' },
};

export default function PortalSent() {
  const [mails, setMails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [resending, setResending] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const load = useCallback(() => {
    setLoading(true);
    outboundApi.list({ page })
      .then(({ data }) => {
        setMails(data.results || data);
        setTotal(data.count || (data.results || data).length);
      })
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const handleResend = async (id) => {
    setResending(true);
    try {
      await outboundApi.resend(id);
      load();
      setSelected(prev => prev ? { ...prev, status: 'queued', error_message: '' } : prev);
    } catch {
      alert('Erneutes Senden fehlgeschlagen.');
    } finally {
      setResending(false);
    }
  };

  const badge = (status) => {
    const s = STATUS_LABELS[status] || { label: status, cls: 'bg-gray-100 text-gray-600' };
    return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.cls}`}>{s.label}</span>;
  };

  return (
    <div className="flex h-full">
      {/* Liste */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <div className="p-4 bg-white border-b border-gray-200 flex items-center justify-between">
          <h1 className="text-lg font-semibold text-gray-900">Gesendet</h1>
          <button onClick={load} className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
            ↻ Aktualisieren
          </button>
        </div>

        <div className="flex-1 overflow-auto">
          {loading ? <LoadingSpinner /> : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 uppercase text-xs sticky top-0">
                <tr>
                  {['An', 'Betreff', 'Von', 'Status', 'Datum'].map(h => (
                    <th key={h} className="px-4 py-3 text-left font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {mails.map(m => (
                  <tr key={m.id}
                    onClick={() => setSelected(m)}
                    className={`cursor-pointer hover:bg-blue-50 ${selected?.id === m.id ? 'bg-blue-50' : ''}`}>
                    <td className="px-4 py-3 text-xs max-w-[160px] truncate">{m.to_addresses}</td>
                    <td className="px-4 py-3 max-w-[220px] truncate">{m.subject || '(kein Betreff)'}</td>
                    <td className="px-4 py-3 text-xs text-gray-500 truncate max-w-[140px]">{m.mail_account_email}</td>
                    <td className="px-4 py-3">{badge(m.status)}</td>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                      {(m.sent_at || m.created_at)
                        ? new Date(m.sent_at || m.created_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
                        : '–'}
                    </td>
                  </tr>
                ))}
                {mails.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-12 text-center text-gray-400">Noch keine gesendeten E-Mails.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        <div className="px-4 py-3 bg-white border-t border-gray-200 flex items-center justify-between text-sm text-gray-500">
          <span>{total} E-Mail{total !== 1 ? 's' : ''}</span>
          <div className="flex gap-2">
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
              className="px-3 py-1 border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50">
              ← Zurück
            </button>
            <span className="px-3 py-1">Seite {page}</span>
            <button disabled={mails.length < 50} onClick={() => setPage(p => p + 1)}
              className="px-3 py-1 border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50">
              Weiter →
            </button>
          </div>
        </div>
      </div>

      {/* Detailansicht */}
      {selected && (
        <div className="w-96 flex-shrink-0 border-l border-gray-200 bg-white flex flex-col">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-sm text-gray-900 truncate">{selected.subject || '(kein Betreff)'}</h3>
            <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600 ml-2 flex-shrink-0">✕</button>
          </div>
          <div className="p-4 space-y-3 text-sm flex-1 overflow-auto">
            <div className="flex items-center gap-2">
              <span className="text-gray-500">Status:</span>
              {badge(selected.status)}
            </div>
            <div><span className="text-gray-500">An:</span> <span className="ml-1 break-all">{selected.to_addresses}</span></div>
            {selected.cc_addresses && (
              <div><span className="text-gray-500">CC:</span> <span className="ml-1 break-all">{selected.cc_addresses}</span></div>
            )}
            <div><span className="text-gray-500">Von:</span> <span className="ml-1">{selected.mail_account_email}</span></div>
            <div><span className="text-gray-500">Erstellt:</span> <span className="ml-1">{new Date(selected.created_at).toLocaleString('de-DE')}</span></div>
            {selected.sent_at && (
              <div><span className="text-gray-500">Gesendet:</span> <span className="ml-1">{new Date(selected.sent_at).toLocaleString('de-DE')}</span></div>
            )}
            {selected.in_reply_to_subject && (
              <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">
                ↩ Antwort auf: <span className="font-medium">{selected.in_reply_to_subject}</span>
              </div>
            )}

            {/* Fehlermeldung */}
            {selected.status === 'failed' && selected.error_message && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="text-xs font-medium text-red-700 mb-1">Fehlermeldung:</div>
                <div className="text-xs text-red-600 break-words">{selected.error_message}</div>
              </div>
            )}

            {/* Anhänge */}
            {selected.new_attachments && selected.new_attachments.length > 0 && (
              <div className="border-t border-gray-100 pt-3">
                <div className="text-xs text-gray-500 mb-2">
                  📎 {selected.new_attachments.length} Anhang{selected.new_attachments.length !== 1 ? 'hänge' : ''}
                </div>
                <div className="space-y-1">
                  {selected.new_attachments.map(att => (
                    <div key={att.id} className="flex items-center justify-between bg-gray-50 rounded px-3 py-2">
                      <span className="text-xs font-medium text-gray-800 truncate flex-1">{att.filename}</span>
                      <span className="text-xs text-gray-400 whitespace-nowrap ml-2">
                        {att.size_bytes > 1048576 ? (att.size_bytes/1048576).toFixed(1)+' MB' : Math.round(att.size_bytes/1024)+' KB'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Mailtext */}
            <div className="border-t border-gray-100 pt-3">
              <div className="text-xs text-gray-500 mb-1">Nachrichtentext:</div>
              <div className="text-sm whitespace-pre-wrap text-gray-700 bg-gray-50 rounded p-3 max-h-80 overflow-auto leading-relaxed font-mono text-xs">
                {selected.body_plain || '(kein Inhalt)'}
              </div>
            </div>
          </div>

          {/* Erneut senden bei Fehler */}
          {selected.status === 'failed' && (
            <div className="p-4 border-t border-gray-100">
              <button
                onClick={() => handleResend(selected.id)}
                disabled={resending}
                className="w-full py-2 bg-red-600 text-white rounded-lg text-xs hover:bg-red-700 disabled:opacity-50"
              >
                {resending ? '⟳ Wird erneut gesendet…' : '↺ Erneut senden'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
